import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Boxes, Calendar, Search, AlertTriangle, CheckCircle2, Loader2, X,
  PackageCheck, Layers, ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface PedidoElegivel {
  pedido_id: number
  pedido_numero: number
  cliente_nome: string | null
  status: string
  forma_pagamento: string | null
  valor_total: number
  criado_em: string | null
  previsao_entrega: string | null
  n_itens_industriais: number
  n_itens_a_produzir: number
}

interface PreviewLinha {
  pedido_id: number
  pedido_numero: number
  cliente_nome: string | null
  forma_pagamento: string | null
  valor_pedido: string
  produto_id: number
  produto_codigo: string | null
  produto_descricao: string | null
  variante: string
  quantidade_pedida: string
  estoque_atual: string
  qtd_a_produzir: string
  ficha_id: number | null
  ficha_ativa: boolean
  motivo_skip: string | null
}

interface MateriaPrima {
  id: number
  codigo: string
  nome: string
}

export default function CargaProducaoPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [previewAberto, setPreviewAberto] = useState(false)
  const [formCarga, setFormCarga] = useState({
    codigo: gerarCodigoSugerido(),
    descricao: '',
    materia_prima_id: '' as number | '',
    semana_referencia: hojeSemanaISO(),
    inicio_previsto: '',
    fim_previsto: '',
    observacao: '',
  })

  const { data: pedidos = [], isLoading } = useQuery<PedidoElegivel[]>({
    queryKey: ['carga-pedidos-elegiveis'],
    queryFn: async () => (await api.get('/industrial/cargas/pedidos-elegiveis')).data,
  })

  const { data: materias = [] } = useQuery<MateriaPrima[]>({
    queryKey: ['materias-primas-select'],
    queryFn: async () => (await api.get('/industrial/materias-primas')).data,
  })

  const filtrados = useMemo(() => {
    if (!busca.trim()) return pedidos
    const q = busca.toLowerCase()
    return pedidos.filter(p =>
      String(p.pedido_numero).includes(q) ||
      (p.cliente_nome || '').toLowerCase().includes(q)
    )
  }, [pedidos, busca])

  const totalSelecionados = useMemo(() => {
    return pedidos.filter(p => selecionados.has(p.pedido_id))
      .reduce((s, p) => s + p.valor_total, 0)
  }, [pedidos, selecionados])

  const previewMut = useMutation<PreviewLinha[], any, number[]>({
    mutationFn: async (ids) => (await api.post('/industrial/cargas/preview', ids)).data,
    onSuccess: () => setPreviewAberto(true),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Falha ao gerar preview'),
  })

  const gerarMut = useMutation({
    mutationFn: async () => {
      if (!formCarga.codigo.trim()) throw new Error('Código da carga é obrigatório')
      const payload: any = {
        codigo: formCarga.codigo.trim(),
        descricao: formCarga.descricao || null,
        materia_prima_id: formCarga.materia_prima_id || null,
        semana_referencia: formCarga.semana_referencia || null,
        inicio_previsto: formCarga.inicio_previsto || null,
        fim_previsto: formCarga.fim_previsto || null,
        observacao: formCarga.observacao || null,
        pedido_ids: Array.from(selecionados),
      }
      return (await api.post('/industrial/cargas/gerar', payload)).data
    },
    onSuccess: (data) => {
      toast.success(`Carga ${data.remessa.codigo} criada — ${data.ops_criadas.length} OP(s)`)
      qc.invalidateQueries({ queryKey: ['carga-pedidos-elegiveis'] })
      qc.invalidateQueries({ queryKey: ['ops'] })
      setSelecionados(new Set())
      setPreviewAberto(false)
      nav(`/industrial/ordens?status=planejada`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || e?.message || 'Falha ao gerar carga'),
  })

  const toggleSel = (id: number) => {
    setSelecionados(prev => {
      const n = new Set(prev)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }
  const selAll = () => {
    if (selecionados.size === filtrados.length) setSelecionados(new Set())
    else setSelecionados(new Set(filtrados.map(p => p.pedido_id)))
  }

  const abrirPreview = () => {
    if (selecionados.size === 0) {
      toast.error('Selecione ao menos 1 pedido')
      return
    }
    previewMut.mutate(Array.from(selecionados))
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900 flex items-center gap-2">
            <Boxes className="h-6 w-6 text-violet-600" />
            Gerar Carga de Produção
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Selecione pedidos liberados sem estoque suficiente. O sistema agrupa por matéria-prima e gera as OPs automaticamente.
          </p>
        </div>
        <button
          onClick={() => nav('/industrial/ordens')}
          className="px-3 py-2 rounded-lg border border-ink-300 hover:bg-ink-50 text-sm"
        >
          Ver Ordens de Produção
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi label="Pedidos elegíveis" value={pedidos.length} cls="bg-sky-50 border-sky-200 text-sky-800" />
        <Kpi label="Selecionados" value={selecionados.size} cls="bg-violet-50 border-violet-200 text-violet-800" />
        <Kpi label="Valor selecionado" value={formatMoney(totalSelecionados)} cls="bg-emerald-50 border-emerald-200 text-emerald-800" />
        <Kpi label="A produzir (itens)" value={pedidos.filter(p => selecionados.has(p.pedido_id)).reduce((s, p) => s + p.n_itens_a_produzir, 0)} cls="bg-amber-50 border-amber-200 text-amber-800" />
      </div>

      <div className="bg-white rounded-lg border border-ink-200 p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs uppercase text-ink-500 font-medium mb-1">Código da carga</label>
            <input
              value={formCarga.codigo}
              onChange={e => setFormCarga(f => ({ ...f, codigo: e.target.value }))}
              className="w-full px-3 py-2 border border-ink-300 rounded text-sm"
              placeholder="REMESSA-2026-W19-EVA-1"
            />
          </div>
          <div>
            <label className="block text-xs uppercase text-ink-500 font-medium mb-1">Matéria-prima principal</label>
            <select
              value={formCarga.materia_prima_id}
              onChange={e => setFormCarga(f => ({ ...f, materia_prima_id: e.target.value ? Number(e.target.value) : '' }))}
              className="w-full px-3 py-2 border border-ink-300 rounded text-sm"
            >
              <option value="">— (sem MP)</option>
              {materias.map(m => <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase text-ink-500 font-medium mb-1">Semana referência</label>
            <input
              type="date"
              value={formCarga.semana_referencia}
              onChange={e => setFormCarga(f => ({ ...f, semana_referencia: e.target.value }))}
              className="w-full px-3 py-2 border border-ink-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase text-ink-500 font-medium mb-1">Início previsto</label>
            <input
              type="date"
              value={formCarga.inicio_previsto}
              onChange={e => setFormCarga(f => ({ ...f, inicio_previsto: e.target.value }))}
              className="w-full px-3 py-2 border border-ink-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase text-ink-500 font-medium mb-1">Fim previsto</label>
            <input
              type="date"
              value={formCarga.fim_previsto}
              onChange={e => setFormCarga(f => ({ ...f, fim_previsto: e.target.value }))}
              className="w-full px-3 py-2 border border-ink-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase text-ink-500 font-medium mb-1">Descrição</label>
            <input
              value={formCarga.descricao}
              onChange={e => setFormCarga(f => ({ ...f, descricao: e.target.value }))}
              className="w-full px-3 py-2 border border-ink-300 rounded text-sm"
              placeholder="REMESSA VERDE - 1 - E.V.A"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            value={busca}
            onChange={e => setBusca(e.target.value)}
            onKeyDown={enterAbrePrimeiro(filtrados, p => toggleSel(p.pedido_id))}
            placeholder="Buscar #pedido ou cliente..."
            className="w-full pl-9 pr-3 py-2 border border-ink-300 rounded-lg text-sm"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-ink-600">{selecionados.size} selecionado(s) — {formatMoney(totalSelecionados)}</span>
          <button
            onClick={abrirPreview}
            disabled={selecionados.size === 0 || previewMut.isPending}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-ink-300 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            {previewMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
            Preview de carga
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-ink-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input type="checkbox"
                    checked={filtrados.length > 0 && selecionados.size === filtrados.length}
                    onChange={selAll} />
                </th>
                <th className="px-3 py-2 text-left">#Pedido</th>
                <th className="px-3 py-2 text-left">Cliente</th>
                <th className="px-3 py-2 text-left">Pgto</th>
                <th className="px-3 py-2 text-right">Valor</th>
                <th className="px-3 py-2 text-center">Itens ind.</th>
                <th className="px-3 py-2 text-center">A produzir</th>
                <th className="px-3 py-2 text-left">Prazo</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} className="text-center py-8 text-ink-500">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                </td></tr>
              )}
              {!isLoading && filtrados.length === 0 && (
                <tr><td colSpan={9} className="text-center py-8 text-ink-500">
                  Nenhum pedido elegível. Pedidos precisam estar aprovados/pagos com itens industriais sem estoque.
                </td></tr>
              )}
              {filtrados.map(p => (
                <tr key={p.pedido_id}
                  onClick={() => toggleSel(p.pedido_id)}
                  className={`border-b border-ink-100 cursor-pointer ${selecionados.has(p.pedido_id) ? 'bg-violet-50' : 'hover:bg-violet-50'}`}>
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selecionados.has(p.pedido_id)} onChange={() => toggleSel(p.pedido_id)} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">#{p.pedido_numero}</td>
                  <td className="px-3 py-2">{p.cliente_nome || <span className="text-ink-400">—</span>}</td>
                  <td className="px-3 py-2 text-xs">{p.forma_pagamento || '-'}</td>
                  <td className="px-3 py-2 text-right">{formatMoney(p.valor_total)}</td>
                  <td className="px-3 py-2 text-center">{p.n_itens_industriais}</td>
                  <td className="px-3 py-2 text-center">
                    {p.n_itens_a_produzir > 0
                      ? <span className="font-semibold text-rose-700">{p.n_itens_a_produzir}</span>
                      : <span className="text-emerald-700">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-600">{p.previsao_entrega || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="text-xs px-2 py-0.5 rounded bg-ink-100">{p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {previewAberto && (
        <PreviewModal
          linhas={previewMut.data || []}
          codigo={formCarga.codigo}
          onClose={() => setPreviewAberto(false)}
          onConfirmar={() => gerarMut.mutate()}
          gerando={gerarMut.isPending}
        />
      )}
    </div>
  )
}

function Kpi({ label, value, cls }: { label: string; value: string | number; cls: string }) {
  return (
    <div className={`rounded-lg border px-4 py-3 ${cls}`}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  )
}

function PreviewModal({ linhas, codigo, onClose, onConfirmar, gerando }: {
  linhas: PreviewLinha[]
  codigo: string
  onClose: () => void
  onConfirmar: () => void
  gerando: boolean
}) {
  // agrupa por produto/variante (uma OP por par)
  const grupos = useMemo(() => {
    const map = new Map<string, { key: string; produto_id: number; produto_codigo: string | null; produto_descricao: string | null; variante: string; ficha_id: number | null; ficha_ativa: boolean; qtd_total: number; pedidos: number[]; motivo_skip: string | null }>()
    for (const l of linhas) {
      const k = `${l.produto_id}::${l.variante}`
      if (!map.has(k)) {
        map.set(k, { key: k, produto_id: l.produto_id, produto_codigo: l.produto_codigo, produto_descricao: l.produto_descricao, variante: l.variante, ficha_id: l.ficha_id, ficha_ativa: l.ficha_ativa, qtd_total: 0, pedidos: [], motivo_skip: l.motivo_skip })
      }
      const g = map.get(k)!
      g.qtd_total += Number(l.qtd_a_produzir || 0)
      if (!g.pedidos.includes(l.pedido_id)) g.pedidos.push(l.pedido_id)
      // se algum item da chave não tem motivo skip, considera elegível
      if (!l.motivo_skip) g.motivo_skip = null
    }
    return Array.from(map.values())
  }, [linhas])

  const elegiveis = grupos.filter(g => !g.motivo_skip && g.qtd_total > 0)
  const ignorados = grupos.filter(g => g.motivo_skip || g.qtd_total === 0)

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2"><Layers className="h-5 w-5 text-violet-600" /> Preview da carga {codigo}</h2>
            <p className="text-xs text-ink-500">{elegiveis.length} OP(s) serão geradas — {ignorados.length} item(ns) serão pulados</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-2 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" /> {elegiveis.length} OP(s) que serão geradas
            </div>
            <table className="w-full text-sm border border-ink-200 rounded">
              <thead className="bg-emerald-50 border-b border-ink-200">
                <tr>
                  <th className="px-3 py-2 text-left">Produto</th>
                  <th className="px-3 py-2 text-left">Variação</th>
                  <th className="px-3 py-2 text-right">Qtd a produzir</th>
                  <th className="px-3 py-2 text-center">Pedidos</th>
                </tr>
              </thead>
              <tbody>
                {elegiveis.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-6 text-ink-500">Nenhum item elegível na seleção atual.</td></tr>
                )}
                {elegiveis.map(g => (
                  <tr key={g.key} className="border-b border-ink-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{g.produto_descricao}</div>
                      <div className="text-xs text-ink-500 font-mono">{g.produto_codigo || `#${g.produto_id}`}</div>
                    </td>
                    <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded bg-ink-100">{g.variante}</span></td>
                    <td className="px-3 py-2 text-right font-semibold">{g.qtd_total.toLocaleString('pt-BR')}</td>
                    <td className="px-3 py-2 text-center text-xs">{g.pedidos.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {ignorados.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-2 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" /> {ignorados.length} item(ns) que serão pulados
              </div>
              <table className="w-full text-xs border border-ink-200 rounded">
                <thead className="bg-amber-50 border-b border-ink-200">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Produto</th>
                    <th className="px-3 py-1.5 text-left">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {ignorados.map(g => (
                    <tr key={g.key} className="border-b border-ink-100">
                      <td className="px-3 py-1.5">
                        {g.produto_descricao} <span className="text-ink-400">({g.produto_codigo})</span>
                      </td>
                      <td className="px-3 py-1.5 text-amber-700">{g.motivo_skip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-ink-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-ink-300 rounded-lg text-sm">Cancelar</button>
          <button
            onClick={onConfirmar}
            disabled={gerando || elegiveis.length === 0}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-ink-300 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            {gerando ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />}
            Confirmar e gerar {elegiveis.length} OP(s)
          </button>
        </div>
      </div>
    </div>
  )
}

function gerarCodigoSugerido(): string {
  const d = new Date()
  const sem = isoWeek(d)
  const ano = d.getFullYear()
  return `REMESSA-${ano}-W${String(sem).padStart(2, '0')}-1`
}

function hojeSemanaISO(): string {
  const d = new Date()
  const monday = new Date(d)
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return monday.toISOString().slice(0, 10)
}

function isoWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const day = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
}
